import {
    Button,
    Drawer,
    DrawerProps,
    Group,
    ScrollArea,
    Select,
    Stack,
} from '@mantine/core';
import {Formik} from 'formik';
import {I18n} from '@aws-amplify/core';

import {
    ReportRunsFilters,
    useReportRunsFilter,
} from '../hooks/useReportRunsFilter';

/**
 * FNP-918: the report runs filter drawer (§7).
 *
 * Formik-driven, matching the form conventions in §6. Filter state lives in the
 * MODULE-SCOPED store (useReportRunsFilter), not component state, so the table
 * — a sibling, not a child — filters the same list.
 *
 * ⚠️ Only `status` is offered, and it is the one field worth a control here:
 * "show me the failed runs" is the question an operator actually opens this
 * page to ask. `reportType` is deliberately left to the keyword box — Stripe
 * defines dozens of report types and the list is not fixed, so a Select would
 * either be wrong or need maintaining against their catalogue.
 *
 * ⚠️ Filtering is CLIENT-SIDE over the loaded window — `listReportRuns` takes
 * no filter argument at all. See useReportRuns.ts.
 */
export const FilterDrawer = ({
                                 opened,
                                 onClose,
                                 ...rest
                             }: DrawerProps) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = useReportRunsFilter();

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-left',
                duration: 750,
                timingFunction: 'ease',
            }}
            position='right'
            offset={10}
            opened={opened}
            onClose={onClose}
            title={I18n.get('Filter Report Runs')}
            padding='xl'
            size='md'
            scrollAreaComponent={ScrollArea.Autosize}
            {...rest}
        >
            <Formik<ReportRunsFilters>
                initialValues={filters}
                enableReinitialize
                onSubmit={(values) => {
                    handleSetFilters(values);
                    onClose();
                }}
            >
                {({
                      values,
                      setFieldValue,
                      handleSubmit,
                      resetForm,
                  }) => (
                    <form onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit();
                    }}>
                        <Stack gap='md'>
                            <Select
                                label={I18n.get('Status')}
                                placeholder={I18n.get('Any')}
                                clearable
                                data={[
                                    {
                                        value: 'pending',
                                        label: I18n.get('Pending'),
                                    },
                                    {
                                        value: 'succeeded',
                                        label: I18n.get('Succeeded'),
                                    },
                                    {
                                        value: 'failed',
                                        label: I18n.get('Failed'),
                                    },
                                ]}
                                value={values.status ?? null}
                                onChange={(value) =>
                                    setFieldValue('status', value ?? '')}
                            />

                            <Group justify='flex-end' mt='md'>
                                {/*
                                  * Clears EVERY field and is reachable from the
                                  * drawer (§7) — it resets both the store and
                                  * the visible form, so the two cannot disagree.
                                  */}
                                <Button
                                    variant='subtle'
                                    onClick={() => {
                                        handleClearFilters();
                                        resetForm({values: {}});
                                    }}
                                >
                                    {I18n.get('Clear filters')}
                                </Button>
                                <Button type='submit'>
                                    {I18n.get('Apply')}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                )}
            </Formik>
        </Drawer>
    );
};
